import { RowActionBehaviour } from '@msdk/definitions'
import { Skeleton, styled, Table, TableBody, TableContainer, TableRow } from '@msdk/ui'
import React from 'react'

import { useDynamicViewContext } from './context'
import { EmptyStateOverlay } from './EmptyStateOverlay'
import { PivotTable } from './PivotTable'
import { Row } from './Row'
import { TableHead } from './TableHeader'
import { StyledTableCell } from './utils'

const ScrollableTableContainer = styled(TableContainer, {
  shouldForwardProp: (prop) => prop !== '$didMeasuredColumns',
})<{
  $didMeasuredColumns?: boolean
}>`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  position: relative;

  > table {
    ${(props) => (props.$didMeasuredColumns ? `min-width: 100%; width: max-content;` : ``)}
    display: flex;
    flex-direction: column;
  }

  > table > thead,
  > table > tbody {
    display: flex;
    flex-direction: column;
  }

  > table > thead > tr,
  > table > tbody > tr {
    display: flex;
    flex-direction: row;
  }
`

const SkeletonCell = ({ colSpan, $width }: { colSpan?: number; $width?: string }) => (
  <StyledTableCell scope="row" size="small" colSpan={colSpan} $visible $width={$width}>
    <Skeleton width="100%" />
  </StyledTableCell>
)

export function DynamicView() {
  const {
    isLoading,
    styleMode,
    viewModel,
    behaviour,
    rows,
    visibleColumns,
    tableRef,
    hideTopBorder,
    multiSelect,
    getColumnWidth,
    didMeasuredColumns,
    isColumnsLayoutInit,
  } = useDynamicViewContext()

  const tableStyle = styleMode ?? 'Default'
  const isEmpty = !isLoading && rows?.length === 0

  const shouldShowRowActions = viewModel.rowActions.behaviour !== RowActionBehaviour.Hide && rows?.length === 0
  const isPivot = !!viewModel.pivotBy

  return (
    <ScrollableTableContainer
      $mode={tableStyle}
      ref={tableRef}
      $hideTopBorder={hideTopBorder}
      $didMeasuredColumns={didMeasuredColumns}
    >
      {isPivot ? (
        isColumnsLayoutInit && <PivotTable />
      ) : (
        <Table>
          <TableHead isColumnsLayoutInit={isColumnsLayoutInit} />
          <TableBody>
            {!isLoading && !isEmpty && isColumnsLayoutInit && rows?.map((row) => <Row row={row} key={row.id} />)}
            {(isLoading || !isColumnsLayoutInit) &&
              Array.from(Array(10).keys()).map((row) => (
                <TableRow key={row}>
                  {multiSelect && <SkeletonCell $width="48px" />}
                  {visibleColumns.map((column) => (
                    <SkeletonCell key={column.fieldName} $width={getColumnWidth(column.fieldName)} />
                  ))}
                  <SkeletonCell colSpan={shouldShowRowActions ? 2 : 1} />
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}
      {isEmpty && <EmptyStateOverlay visualization={behaviour?.emptyState} />}
    </ScrollableTableContainer>
  )
}
